import { useMemo, useState } from 'react';
import { useSensitivityGrid, usePortfolioSensitivityGrid } from '../../hooks/useCalculations';
import { useSimulatorStore } from '../../stores/useSimulatorStore';
import { PRICE_POINTS, S1_LEAD_LEVELS } from '../../data/conjointData';
import { formatPrice, formatNumber, formatCurrencyCompact } from '../../lib/formatters';
import { getPnLColor } from '../../styles/chart-theme';
import { InfoButton } from '../common/Tooltip';

interface Props {
 viewMode?: 'single' | 'portfolio';
 demandMode?: 'ceiling' | 'split';
}

export function SensitivityHeatmap({ viewMode = 'single' }: Props) {
 if (viewMode === 'portfolio') {
 return <PortfolioSensitivityView />;
 }
 return <SingleProductSensitivityView />;
}

function SingleProductSensitivityView() {
 const grid = useSensitivityGrid();
 const currentS1 = useSimulatorStore(s => s.assumptions.s1Leads);
 const [hovered, setHovered] = useState<{ row: number; col: number } | null>(null);

 const { min, max } = useMemo(() => {
 let min = Infinity, max = -Infinity;
 grid.forEach(row => row.forEach(cell => { if (cell.pnl < min) min = cell.pnl; if (cell.pnl > max) max = cell.pnl; }));
 return { min, max };
 }, [grid]);

 const closestS1Col = S1_LEAD_LEVELS.reduce((best, s1, i) => Math.abs(s1 - currentS1) < Math.abs(S1_LEAD_LEVELS[best] - currentS1) ? i : best, 0);

 return (
 <div className="card">
 <div className="flex items-center gap-2 mb-1">
 <h3 className="text-heading font-semibold text-secondary uppercase tracking-wider">Profitability Matrix: Price x S1 Leads — Single Product</h3>
 <InfoButton helpKey="sensitivityHeatmap" size={13} />
 </div>
 <p className="text-sm text-muted mb-4">Single-product P&L at base conjoint choice rates (no feature lift applied) with full fixed costs. Green = profit, Red = loss. Current S1: <span className="font-mono">{formatNumber(currentS1)}</span>. <span className="italic">Note: Your configured packages include feature lift, so their numbers will differ from this baseline view.</span></p>
 <div className="overflow-x-auto">
 <table className="w-full border-collapse">
 <thead>
 <tr>
 <th className="py-2 px-3 text-left text-xs text-secondary font-medium">Price \ S1 Leads</th>
 {S1_LEAD_LEVELS.map((s1, ci) => (
 <th key={s1} className={`py-2 px-2 text-center text-xs font-mono font-medium ${ci === closestS1Col ? 'text-chart-teal' : 'text-secondary'}`}>{formatNumber(s1)}</th>
 ))}
 </tr>
 </thead>
 <tbody>
 {grid.map((row, ri) => (
 <tr key={PRICE_POINTS[ri].price}>
 <td className="py-1 px-3 text-sm font-mono text-primary font-medium">{formatPrice(PRICE_POINTS[ri].price)}</td>
 {row.map((cell, ci) => {
 const isHovered = hovered?.row === ri && hovered?.col === ci;
 const isCurrent = ci === closestS1Col;
 return (
 <td key={ci} className="py-1 px-1 text-center relative" onMouseEnter={() => setHovered({ row: ri, col: ci })} onMouseLeave={() => setHovered(null)}>
 <div className={`rounded px-2 py-2 font-mono text-xs transition-all ${isHovered ? 'ring-2 ring-white/40 z-10' : ''} ${isCurrent ? 'ring-1 ring-chart-teal/40' : ''}`} style={{ backgroundColor: getPnLColor(cell.pnl, min, max) }}>
 <span className={cell.pnl >= 0 ? 'text-emerald-100' : 'text-red-100'}>{formatCurrencyCompact(cell.pnl)}</span>
 </div>
 {isHovered && (
 <div className="absolute z-50 bottom-full left-1/2 -translate-x-1/2 mb-2 rounded-lg shadow-xl px-3 py-2 text-xs text-left whitespace-nowrap pointer-events-none"
 style={{ backgroundColor: 'var(--color-tooltip-bg)', border: '1px solid var(--color-tooltip-border)' }}>
 <div className="text-secondary font-mono">{formatPrice(cell.price)} x {formatNumber(cell.s1Leads)} <span className="font-sans">S1 leads</span></div>
 <div className="text-primary mt-1">S2 Units: <span className="font-mono">{formatNumber(cell.s2Units)}</span></div>
 <div className="text-primary">Net Rev: <span className="font-mono">{formatCurrencyCompact(cell.netRevenue)}</span></div>
 <div className={cell.pnl >= 0 ? 'text-metric-positive' : 'text-metric-negative'}>P&L: <span className="font-mono font-bold">{formatCurrencyCompact(cell.pnl)}</span></div>
 </div>
 )}
 </td>
 );
 })}
 </tr>
 ))}
 </tbody>
 </table>
 </div>
 <div className="flex items-center gap-3 mt-4 text-xs text-muted">
 <span>Color scale:</span>
 <div className="flex items-center gap-1"><div className="w-4 h-3 rounded" style={{ backgroundColor: getPnLColor(min, min, max) }} /><span className="font-mono">{formatCurrencyCompact(min)}</span></div>
 <div className="w-16 h-3 rounded" style={{ background: `linear-gradient(to right, ${getPnLColor(min, min, max)}, var(--color-pnl-neutral), ${getPnLColor(max, min, max)})` }} />
 <div className="flex items-center gap-1"><div className="w-4 h-3 rounded" style={{ backgroundColor: getPnLColor(max, min, max) }} /><span className="font-mono">{formatCurrencyCompact(max)}</span></div>
 </div>
 </div>
 );
}

function PortfolioSensitivityView() {
 const data = usePortfolioSensitivityGrid();
 const currentS1 = useSimulatorStore(s => s.assumptions.s1Leads);
 const packages = useSimulatorStore(s => s.packages);
 const [hovered, setHovered] = useState<number | null>(null);

 const { min, max } = useMemo(() => {
 let min = Infinity, max = -Infinity;
 data.forEach(d => { if (d.pnl < min) min = d.pnl; if (d.pnl > max) max = d.pnl; });
 return { min, max };
 }, [data]);

 const closestIdx = data.reduce((best, d, i) => Math.abs(d.s1Leads - currentS1) < Math.abs(data[best].s1Leads - currentS1) ? i : best, 0);

 return (
 <div className="card">
 <div className="flex items-center gap-2 mb-1">
 <h3 className="text-heading font-semibold text-secondary uppercase tracking-wider">Portfolio Sensitivity: S1 Leads → Portfolio P&L</h3>
 <InfoButton helpKey="sensitivityHeatmap" size={13} />
 </div>
 <p className="text-sm text-muted mb-4">
 Portfolio P&L (split demand, fixed costs once) as S1 leads vary. Current config: {packages.length} packages. Current S1: <span className="font-mono">{formatNumber(currentS1)}</span>.
 </p>
 <div className="overflow-x-auto">
 <table className="w-full border-collapse">
 <thead>
 <tr>
 <th className="py-2 px-3 text-left text-xs text-secondary font-medium">S1 Leads</th>
 <th className="py-2 px-3 text-right text-xs text-secondary font-medium">Total S2 Units</th>
 <th className="py-2 px-3 text-right text-xs text-secondary font-medium">Net Revenue</th>
 <th className="py-2 px-3 text-right text-xs text-secondary font-medium">Portfolio P&L</th>
 </tr>
 </thead>
 <tbody>
 {data.map((d, i) => {
 const isHovered = hovered === i;
 const isCurrent = i === closestIdx;
 return (
 <tr key={d.s1Leads}
 onMouseEnter={() => setHovered(i)}
 onMouseLeave={() => setHovered(null)}
 className={`border-b border-border/30 transition-colors ${isCurrent ? 'ring-1 ring-inset ring-chart-teal/30' : ''}`}
 style={isHovered ? { backgroundColor: 'var(--color-hover-bg)' } : undefined}
 >
 <td className={`py-2 px-3 font-mono text-sm ${isCurrent ? 'text-chart-teal font-semibold' : 'text-primary'}`}>
 {formatNumber(d.s1Leads)}
 {isCurrent && <span className="text-micro text-chart-teal ml-1">(current)</span>}
 </td>
 <td className="py-2 px-3 text-right font-mono text-sm text-primary">{formatNumber(d.totalUnits)}</td>
 <td className="py-2 px-3 text-right font-mono text-sm text-primary">{formatCurrencyCompact(d.totalRevenue)}</td>
 <td className="py-2 px-3 text-right">
 <span className={`font-mono text-sm font-semibold px-2 py-1 rounded ${d.pnl >= 0 ? 'text-metric-positive' : 'text-metric-negative'}`}
 style={{ backgroundColor: getPnLColor(d.pnl, min, max) }}>
 {formatCurrencyCompact(d.pnl)}
 </span>
 </td>
 </tr>
 );
 })}
 </tbody>
 </table>
 </div>
 </div>
 );
}
